import React, { useLayoutEffect, useState } from 'react';
import { useRouteError } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import LoadingScreen from './LoadingScreen';
import { isChunkLoadError, reloadPageForStaleChunks } from '../../Utils/chunkLoadError';

const getRouteErrorMessage = (error) => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unexpected error occurred.';
};

const RouteChunkErrorBoundary = () => {
  const error = useRouteError();
  const [reloadBlocked, setReloadBlocked] = useState(false);

  const chunkError = isChunkLoadError(error);

  useLayoutEffect(() => {
    if (!chunkError) {
      return;
    }

    const reloaded = reloadPageForStaleChunks();
    if (!reloaded) {
      setReloadBlocked(true);
    }
  }, [chunkError]);

  if (chunkError && !reloadBlocked) {
    return <LoadingScreen position="fixed" title="Updating application..." />;
  }

  if (chunkError && reloadBlocked) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          px: 2,
        }}
      >
        <Typography variant="h6" color="text.primary">
          A new version of the application is available.
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Please refresh the page to load the latest updates.
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Refresh
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
        px: 2,
      }}
    >
      <Typography variant="h6" color="text.primary">
        Something went wrong.
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {getRouteErrorMessage(error)}
      </Typography>
      <Button variant="contained" href="/">
        Go to home
      </Button>
    </Box>
  );
};

export default RouteChunkErrorBoundary;
